import { initializeApp } from "firebase/app";
import { getFirestore, CACHE_SIZE_UNLIMITED } from "firebase/firestore";
import { getAuth, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getAnalytics } from "firebase/analytics";
import { toast } from "sonner";
import { firebaseConfig } from "./config";

// Use const for app-level instances
const app = initializeApp(firebaseConfig);

// Correct Firestore initialization
const db = getFirestore(app);

const auth = getAuth(app);

// Define connectionStateListeners
const connectionStateListeners = new Set<(isConnected: boolean) => void>();

// Firebase app instance variables and connection state tracking
let usersCollection;
let referralsCollection;

// Connection state tracking
export const connectionState = {
  isConnected: false,
  lastError: null
};

// Connection notification function
const notifyConnectionChange = (isConnected: boolean): void => {
  connectionStateListeners.forEach(listener => listener(isConnected));
};

export const onConnectionStateChange = (callback: (isConnected: boolean) => void): (() => void) => {
  connectionStateListeners.add(callback);
  return () => {
    connectionStateListeners.delete(callback);
  };
};

// Export initialization function
let isInitialized = false;

export const initializeFirebase = async (): Promise<void> => {
  if (isInitialized) return;

  try {
    await setPersistence(auth, browserLocalPersistence);
    await db.enableNetwork();
    isInitialized = true;
    console.log("Firebase initialized successfully");
  } catch (error) {
    console.error("Firebase initialization error:", error);
    throw error;
  }
};

// Add connection monitor for browser environments
export const monitorConnection = (): void => {
  if (typeof window === 'undefined') return;
  
  window.addEventListener('online', async () => {
    console.log('Network connection restored, reconnecting to Firestore...');
    try {
      if (db) {
        await db.enableNetwork();
        connectionState.isConnected = true;
        connectionState.lastError = null;
        notifyConnectionChange(true);
        toast.success("You're back online");
      }
    } catch (e) {
      console.error('Failed to reconnect to Firestore:', e);
    }
  });
  
  window.addEventListener('offline', () => {
    console.log('Network connection lost');
    connectionState.isConnected = false;
    notifyConnectionChange(false);
    toast.error("You're offline. Some features may be unavailable.");
  });
};

// Export online status checker
export const checkOnlineStatus = (): boolean => {
  return connectionState.isConnected && (typeof navigator === 'undefined' || navigator.onLine);
};

// Reset Firestore connection for 400 errors
export const resetFirestoreConnection = async (): Promise<boolean> => {
  if (!db) return false;
  
  try {
    await db.terminate();
    await db.enableNetwork();
    console.log("Firestore connection reset successfully");
    connectionState.isConnected = true;
    notifyConnectionChange(true);
    return true;
  } catch (error) {
    console.error("Failed to reset Firestore connection:", error);
    connectionState.isConnected = false;
    notifyConnectionChange(false);
    return false;
  }
};

// Initialize Firebase immediately to avoid "undefined" errors
(async () => {
  try {
    await initializeFirebase();
    if (typeof window !== 'undefined') {
      monitorConnection();
    }
  } catch (error) {
    console.error('Could not initialize Firebase:', error);
    // Don't throw here, allow graceful fallback
  }
})();

// Monitor connection
window?.addEventListener('online', async () => {
  try {
    await db?.enableNetwork();
    connectionState.isConnected = true;
    toast.success("You're back online");
  } catch (e) {
    console.error('Failed to reconnect:', e);
  }
});

window?.addEventListener('offline', () => {
  connectionState.isConnected = false;
  toast.error("You're offline");
});

// Export instances
export { app, db, auth, usersCollection, referralsCollection, isInitialized };
export { isProductionDomain } from './config';
